import { Component, OnInit } from '@angular/core';
import { IAttachment } from '@common/interfaces/attachment';
import { AppService } from '@ep-om/app.service';
import { requiredAttachemntValidator } from '@ep-om/project/components/validators/angular-forms/custom-validators';
import { AttachmentService } from '@ep-om/project/state/attachment/attachment.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FieldType } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map, startWith } from 'rxjs/operators';

export const allowedExt = ['jpg', 'jpeg', 'png', 'svg'];

export type PhotoVM = {
  attachment: IAttachment;
  base64Thumbnail: string;
};

const SEP = ',';

@Component({
  selector: 'formly-foto-upload',
  templateUrl: './formly-foto-upload.component.html'
})
@UntilDestroy()
export class FormlyFotoUploadComponent extends FieldType implements OnInit {
  vm$: Observable<PhotoVM[]>;

  constructor(
    private _attachmentService: AttachmentService,
    private appService: AppService,
    private nzns: NzNotificationService,
    private translateService: TranslateService
  ) {
    super();
  }

  ngOnInit(): void {
    if (!!this.props?.required) {
      this.formControl.addValidators(requiredAttachemntValidator);
    } else {
      this.formControl.clearValidators();
    }
    this.formControl.updateValueAndValidity();
    
    this._initVm();
  }

  _initVm(): void {
    this.vm$ = this.formControl.valueChanges
      .pipe(untilDestroyed(this),
        startWith(this.formControl.value),
        distinctUntilChanged(),
        map(value => {
          const ids = value ? String(value).split(SEP) : [];
          return this._attachmentService.query.getByIssueId(this.model.issueId).filter(att => ids.includes(att.id) && att?.interfaceId === this.model.id);
        }),
        map((attachments) =>
        attachments.filter((att) => {
          const lastDot: number = att.fileName.lastIndexOf('.');
          const fileExtension: string = lastDot !== -1 ? att.fileName.slice(lastDot + 1).toLocaleLowerCase() : null;
          return !att?.hidden && allowedExt.includes(fileExtension);
        })
      ),
      map((attachments) =>
        attachments.map((att) => ({
          attachment: att,
          base64Thumbnail: this._attachmentService.bufferToBase64(att.thumbnail)
        }))
      )
    )
  }

  handleUpload = async (item: any) => {
    const fileSize = item.file.size;
    const fileSizeLimit = this.appService.SETTINGS.attachmentOptions?.limitSize;
    const isAboveSizeLimit = fileSize / 1024 / 1024 > fileSizeLimit;
    if (isAboveSizeLimit) {
      this.nzns.error(this.translateService.instant('ERROR'), this.translateService.instant('ATTACHMENTS.ABOVE_MAX_LIMIT', { fileSizeLimit }));
      return;
    }

    const alreadyExists = this._attachmentService.query
      .getByFileName(item.file.name)
      .some((file) => file.issueId === this.model?.issueId && !file.hidden);
    if (alreadyExists) {
      this.nzns.error(this.translateService.instant('ERROR'), this.translateService.instant('ATTACHMENTS.ALREADY_EXISTING'));
      return;
    }

    const fileExtension: string = this._attachmentService.getFileExtension(item.file.name);
    if (!allowedExt.includes(fileExtension)) {
      this.nzns.error(this.translateService.instant('ERROR'), this.translateService.instant('ATTACHMENTS.UNSUPPORTED_FORMAT'));
      return;
    }
    await this._attachmentService.createPhotoAttachment(item.file, this.model?.issueId, this.model.id)
      .then((res) => this._addFormAttachment(res.createdAttachment.id))
      .catch(err => console.error(err));    
  };

  _deleteAttachment(attId: string): void {
    if (this.formControl.disabled) {
      this.nzns.error(this.translateService.instant('TOAST.ERROR'), this.translateService.instant('ATTACHMENTS.FORBIDDEN_ACTION'));
      return;
    }
    this._attachmentService.trash(false, attId, (response) => {
      if (response.status === 'success') {
        this._removeFormAttachment(attId);
        this.nzns.success(this.translateService.instant('TOAST.SUCCESS'), this.translateService.instant('ATTACHMENTS.DELETE_SUCCESS'));
      } else {
        this.nzns.error(this.translateService.instant('TOAST.ERROR'), this.translateService.instant('ATTACHMENTS.DELETE_FAILED'));
      }
    });
  }

  async _showAttachments(vm: PhotoVM) {
    const fileExtension = this._attachmentService.getFileExtension(vm.attachment.fileName);
    if (vm.attachment?.thumbnail) {
      this._openPhotoModal(vm.attachment);
    } else if (allowedExt.includes(fileExtension)) {
      await this._attachmentService.showFile(vm.attachment, fileExtension);
    } else {
      this._downloadAttachment(vm.attachment);
    }
  }

  _downloadAttachment(attachment: IAttachment) {
    this._attachmentService.downloadAttachment(attachment);
  }

  private async _openPhotoModal(attachment: IAttachment) {
    this._attachmentService.openPhotoModal(attachment, false);
  }

  private _removeFormAttachment(attachmentId: string): void {
    const oldValue = this.formControl?.value ? String(this.formControl.value).split(SEP) : [];
    const filtered = oldValue.filter((val) => val !== attachmentId).join(SEP);
    this.formControl.patchValue(filtered);
    this.formControl.markAsTouched();
  }

  private _addFormAttachment(attachmentId: string): void {
    const oldValue = this.formControl?.value ? String(this.formControl.value).split(SEP) : [];
    if (!oldValue.includes(attachmentId)) {
      oldValue.push(attachmentId);
    }
    this.formControl.patchValue(oldValue.join(SEP));
    this.formControl.markAsTouched();
  }
}
